"use client";

import React, { useMemo } from "react";
import { getColorLinear, getColor } from "@/static/colors";
import { cn } from "@/lib/utils";

/**
 * A reusable Color Swatch component.
 * 
 * @param {string} code - Color code (e.g., 'titanium')
 * @param {boolean} isSelected - Whether the swatch is selected (for filters)
 * @param {string} className - Additional classes
 * @param {Object} props - Other props (onClick, etc.)
 */
const ColorSwatch = React.memo(({ 
  code, 
  isSelected = false, 
  className = "",
  ...props 
}) => {
  const colorLinear = useMemo(() => getColorLinear(code), [code]);

  return (
    <div 
      className={cn(
        "relative flex items-center justify-center transition-all group active:scale-95 w-7 h-7 md:w-9 md:h-9 rounded-lg",
        isSelected ? "scale-110" : "hover:scale-105",
        className
      )}
      {...props}
    >
      {/* Outer Glow/Border layer */}
      <div 
        className={cn(
          "absolute inset-0 transition-opacity duration-150 rounded-lg",
          isSelected ? "opacity-100 border-[1.5px] md:border-2 border-[#D93451]" : "opacity-40 group-hover:opacity-100"
        )}
        style={{ background: isSelected ? "transparent" : colorLinear }}
      />
      
      {/* Gap layer (matches background) */}
      <div className={cn("absolute bg-[#0d0d0e] inset-[1.5px] md:inset-[2px] rounded-[6px]")} />

      {/* Color swatch */}
      <div 
        className={cn("absolute inset-[3px] md:inset-[4px] rounded-[5px]")}
        style={{ background: colorLinear }}
      />
      
      {/* Special handling for white to show border */}
      {code === "white" && (
        <div className={cn("absolute border border-gray-300 inset-[3px] md:inset-[4px] rounded-[5px]")} />
      )}
    </div>
  );
});

ColorSwatch.displayName = "ColorSwatch";

export default ColorSwatch;

